// frontend/src/services/agent/AgentMapper.ts
/**
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 🚀 NeuroAgentes Platform - Intelligent Agent Development
 * 
 * AUDITORIA: Mapper para transformação de dados de agentes.
 * Centraliza mapeamento entre formatos de API e interfaces do frontend.
 */

import { Agent, AgentConfiguration } from "../../types";
import { createLogger } from "../../utils/logger";

const logger = createLogger('AgentMapper');

// Interface para dados brutos da API
export interface RawAgentData {
  id?: string;
  nome?: string;
  name?: string;
  descricao?: string;
  description?: string;
  status?: string;
  avatar?: string;
  projetoId?: string;
  projectId?: string;
  projeto_id?: string;
  criado_em?: string;
  created_at?: string;
  createdAt?: string;
  atualizado_em?: string;
  updated_at?: string;
  updatedAt?: string;
  ultimo_ativo_em?: string;
  last_active?: string;
  lastActive?: string;
  plataforma_id?: number;
  platformId?: number;
  platform_id?: number;
  whatsapp_number?: string;
  whatsappNumber?: string;
  webhook_url?: string;
  webhookUrl?: string;
  isConnected?: boolean;
  is_connected?: boolean;
  setor_negocio_id?: string;
  setorNegocioId?: string;
  descricao_funcao?: string;
  descricaoFuncao?: string;
  provedor?: string; // 'twilio' | 'baileys' | 'whatsapp-web-js'
  [key: string]: unknown;
}

/**
 * Classe responsável pelo mapeamento de dados de agentes
 */
export class AgentMapper {
  /**
   * Mapeia dados brutos da API para interface Agent
   */
  static mapToAgent(rawData: RawAgentData, projectId?: string): Agent {
    logger.debug('Mapeando dados do agente', { hasId: !!rawData.id });

    // Mapear campos com diferentes naming conventions
    const agent: Agent = {
      id: rawData.id || '',
      nome: rawData.nome || rawData.name || '',
      descricao: rawData.descricao || rawData.description || '',
      status: this.normalizeStatus(rawData.status),
      
      // Project ID com fallback
      projeto_id: rawData.projeto_id || 
                 rawData.projectId || 
                 rawData.projetoId || 
                 projectId || undefined,
      
      // Timestamps com normalização
      criado_em: this.normalizeTimestamp(
        rawData.criado_em || rawData.created_at || rawData.createdAt
      ),
      
      atualizado_em: this.normalizeTimestamp(
        rawData.atualizado_em || rawData.updated_at || rawData.updatedAt
      ),
      
      ultimo_ativo_em: this.normalizeTimestamp(
        rawData.ultimo_ativo_em || rawData.last_active || rawData.lastActive
      ),
      
      // Platform ID com normalização
      plataforma_id: typeof rawData.plataforma_id === 'number' ? rawData.plataforma_id :
                    typeof rawData.platformId === 'number' ? rawData.platformId :
                    typeof rawData.platform_id === 'number' ? rawData.platform_id : 
                    1, // Default WhatsApp
      
      // Campos alternativos para compatibilidade
      projetoId: rawData.projetoId || rawData.projectId || rawData.projeto_id || projectId || undefined,
      plataformaId: typeof rawData.platformId === 'number' ? rawData.platformId :
                   typeof rawData.plataforma_id === 'number' ? rawData.plataforma_id :
                   typeof rawData.plataformaId === 'number' ? rawData.plataformaId : 1,
      name: rawData.name || rawData.nome || '',
      description: rawData.description || rawData.descricao || '',
      
      // Campos específicos do workspace otimizado (schema banco)
      setor_negocio_id: rawData.setor_negocio_id || rawData.setorNegocioId || undefined,
      descricao_funcao: rawData.descricao_funcao || rawData.descricaoFuncao || undefined,
      avatar: rawData.avatar || undefined,

      // Campo provedor (para distinguir entre Twilio, Baileys, WhatsApp Web)
      provedor: rawData.provedor || undefined,
      
      // Configuração incluindo webhook_url e telefone
      configuracao: {
        modo_operacao: rawData.modo_operacao || 'seletivo',
        configuracoes_seletivas: rawData.configuracoes_seletivas || {
          palavras_chave: [],
          intencoes: [],
          tempo_inatividade: 0
        },
        telefone: rawData.whatsapp_number || rawData.whatsappNumber,
        webhook_url: rawData.webhook_url || rawData.webhookUrl
      } as AgentConfiguration
    };

    // Validar campos obrigatórios
    if (!agent.id || !agent.nome) {
      logger.warn('Agente com dados incompletos', { 
        hasId: !!agent.id, 
        hasNome: !!agent.nome 
      });
    }

    return agent;
  }

  /**
   * Mapeia lista de dados brutos para lista de agentes
   */
  static mapToAgentList(rawDataList: RawAgentData[], projectId?: string): Agent[] {
    logger.debug(`Mapeando ${rawDataList.length} agentes`);

    return rawDataList
      .map(rawData => this.mapToAgent(rawData, projectId))
      .filter(agent => agent.id && agent.nome); // Filtrar agentes válidos
  }

  /**
   * Normaliza status do agente
   */
  private static normalizeStatus(status?: string): Agent['status'] {
    if (!status) return 'inativo';
    
    const normalizedStatus = status.toLowerCase();
    
    switch (normalizedStatus) {
      case 'ativo':
      case 'active':
        return 'ativo';
      
      case 'inativo':
      case 'inactive':
        return 'inativo';
      
      case 'pausado':
      case 'paused':
      case 'training':
        return 'pausado';
      
      default:
        logger.debug('Status desconhecido, usando inativo', { status });
        return 'inativo';
    }
  }

  /**
   * Normaliza timestamp para formato ISO string
   */
  private static normalizeTimestamp(timestamp?: string | number | Date): string | undefined {
    if (!timestamp) return undefined;
    
    try {
      return new Date(timestamp).toISOString();
    } catch (error) {
      logger.debug('Erro ao normalizar timestamp', { timestamp });
      return undefined;
    }
  }

  /**
   * Mapeia dados do frontend para payload da API
   */
  static mapToAPIPayload(agent: Partial<Agent>): Record<string, unknown> {
    logger.debug('Mapeando para payload da API');

    const payload: Record<string, unknown> = {};

    // Mapear apenas campos definidos
    if (agent.nome !== undefined) payload.name = agent.nome;
    if (agent.descricao !== undefined) payload.description = agent.descricao;
    if (agent.status !== undefined) payload.status = agent.status;
    if (agent.projetoId !== undefined) payload.projectId = agent.projetoId;
    if (agent.projeto_id !== undefined) payload.projectId = agent.projeto_id;
    if (agent.plataforma_id !== undefined) payload.platformId = agent.plataforma_id;
    if (agent.configuracao?.telefone !== undefined) payload.whatsappNumber = agent.configuracao.telefone;
    if (agent.configuracao?.webhook_url !== undefined) payload.webhookUrl = agent.configuracao.webhook_url;

    return payload;
  }
}